import React, { useState } from 'react';

import { Colors, Shadows } from '@domain/theming';
import { styled } from '@mui/material/styles';

import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { motion } from "motion/react"
import Button from '../Button';
import DialogButton from '@mui/material/Button';
import Container from '../Container';
import Text from '../Text';
import { companyNames, proccessType } from '@domain/constants';
import { useSelector } from 'react-redux';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

const ConsentBanner = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 3;
  width: 100%;
  color: ${Colors.BLACK};
  flex-direction: column;
  > div {
    padding: 30px 40px 50px 40px;
    width: 88%;
    max-width: 1200px;
    margin: auto;
    position: relative;
    top: 30px;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    background-color: ${Colors.GREY_LIGHT};
    box-shadow: ${Shadows.SHADOW4};
  }
`;

const ContainerStyled = styled(Container)`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
`;

const BannerButton = styled(Button)`
  width: 120px;
  text-transform: initial;
  flex: 0 1 auto;
  padding: 10px;
  height: auto;
  border-radius: 15px;
  margin-right: 20px;
  display: flex;
`;
const Link = styled('a')`
  color: ${Colors.BLACK};
  text-decoration: underline;
  cursor: pointer;
  padding: 10px 0 12px;
`;

const TableStyled = styled(Table)`
  margin-bottom: 40px;
`;
const LinkBlock = styled(Link)`
  display: block;
`;

const variants = {
  open: {
    opacity: 1,
    transition: {
      duration: .3,
      delay: .3,
    }
  },
  closes: {
    opacity: 0,
    transition: {
      duration: 1,
    }
  }
}


const categories = {
  "Analytics": [{
    category: 'Analytics',
    description: `Google Analytics 4 (GA4) is the next generation of Analytics which collects event-based data from both websites and apps`,
    id: 'ga',
    name: 'Google Analytics 4',
  }],
  "Heatmaps & Recordings": [{
    category: 'Heatmaps & Recordings',
    description: `Hotjar is a heatmap and recording tool for tracking what your users are doing on your website, and where they're doing it`,
    id: 'hotjar',
    name: 'Hotjar',
  }]
}
const ConsentDialogContent = ({
  preferences,
  updatePreferences
}) => (
  <>
    <Container margin="10px 0 20px">
      <Text>
        We gebruiken de data verzameld door de cookies en de JavaScript
        Libraries om uw ervaring met de website te verbeteren, website-verkeer
        te analyzeren, en de algemene prestatie van de website te verhogen.
      </Text>
    </Container>
    <TableStyled size="small">
      <TableHead>
        <TableRow>
          <TableCell>Allow</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Purpose</TableCell>
          <TableCell>Tool Name</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.keys(categories).map((category) => {
          return (categories[category].map((destination) => (
            <TableRow key={destination.id}>
              <TableCell component="th" scope="row">
                <Checkbox
                  checked={Boolean(preferences[destination.id])}
                  onChange={() => updatePreferences(destination.id)}
                />
              </TableCell>
              <TableCell>{destination.category}</TableCell>
              <TableCell>{destination.description}</TableCell>
              <TableCell>{destination.name}</TableCell>
            </TableRow>
          )))
        })}
      </TableBody>
    </TableStyled>
  </>
);



const Cookie = ({
  preferences,
  updateConsent,
  updatePreferences
}) => {

  const consent = useSelector((state) => state.consentCode)
  const branding = useSelector((state) => state.insurer)

  const [dialogOpen, setDialogOpen] = useState(false);

  const isSG = branding && branding.objectType === companyNames.Schadegarant
  const isDealerDistribution = branding && branding.objectType === proccessType.DealerDistribution
  const isDealerSaas = branding && branding.objectType === proccessType.DealerSass

  const writeKeyHotjar = process.env.REACT_APP_HOTJAR_API_KEY
  const writeKeyGA = process.env.REACT_APP_GA_API_KEY

  if (consent !== null) {
    return <></>
  }
  if (!writeKeyHotjar && !writeKeyGA) {
    return <></>
  }
  return (
    <>
      <ConsentBanner
        animate={'open'}
        variants={variants}
      >
        <Container flexDirection="column">
          <Text>
            We gebruiken cookies (en andere dergelijke technologieën) om
            data te verzamelen zodat wij uw ervaring op onze site kunnen
            verbeteren. Door gebruik te maken van onze website gaat u
            akkoord met het verzamelen van data zoals beschreven in onze{' '}
            <Link
              href="https://8749308.fs1.hubspotusercontent-na1.net/hubfs/8749308/Privacy-Statement-Openclaims.pdf"
              rel="noopener noreferrer"
              target="_blank"
            >
              {' '}
              Website Data Collection Policy
            </Link>
            .
          </Text>
          {isSG && <Text> Wilt u weten waar Schadegarant uw data voor nodig heeft, klik dan{' '}
            <Link
              href="https://www.schadegarant.nl/privacystatement/"
              rel="noopener noreferrer"
              target="_blank"
            >
              {' '}
              hier
            </Link>.</Text>}
          {isDealerDistribution && <Text> Wilt u weten waar Bovemij uw data voor nodig heeft, klik dan{' '}
            <Link
              href="https://www.bovemij.nl/privacyverklaring"
              rel="noopener noreferrer"
              target="_blank"
            >
              {' '}
              hier
            </Link>.</Text>}
          {isDealerSaas && <Text><br /> Wij hebben uw persoonsgegevens nodig om uw schade te behandelen. Deze geven we door aan <Link
            href="https://www.centraalbeheer.nl/over-ons/zo-werken-wij/privacystatement"
            rel="noopener noreferrer"
            target="_blank"
          >
            Stichting CIS
          </Link>. In ons <Link
            href="https://www.carmeleon.info/over-carmeleon/privacyverklaring"
            rel="noopener noreferrer"
            target="_blank"
          >
              privacy statement
            </Link> vindt u meer informatie over Stichting CIS en uw rechten daarbij.</Text>}
          <ContainerStyled>
            <BannerButton type="button" onClick={() => updateConsent({ ga: true, hotjar: true })}>
              Ok
            </BannerButton>
            <LinkBlock onClick={() => setDialogOpen(true)}>
              Click hier om uw voorkeuren te wijzigen
            </LinkBlock>
          </ContainerStyled>
        </Container>
      </ConsentBanner>
      <div>
        <Dialog
          open={dialogOpen}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Voorkeuren voor dataverzameling van deze site"}</DialogTitle>
          <DialogContent>
            <ConsentDialogContent
              preferences={preferences}
              updateConsent={updateConsent}
              updatePreferences={updatePreferences}
            />
          </DialogContent>
          <DialogActions>
            <DialogButton onClick={() => setDialogOpen(false)} color="primary" autoFocus>
              Annuleren
            </DialogButton>
            <DialogButton onClick={() => {
              updateConsent(preferences)
              // setDialogOpen(false)
            }} color="primary">
              Opslaan
            </DialogButton>

          </DialogActions>
        </Dialog>
      </div>

    </>
  );
};

export default Cookie;
